"use client";

import {
  DEFAULT_SCRIPT_ID,
  SCRIPT_URL,
  Turnstile,
  type TurnstileInstance,
} from "@marsidev/react-turnstile";
import * as Sentry from "@sentry/nextjs";
import { useLocale, useTranslations } from "next-intl";
import { usePostHog } from "posthog-js/react";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  createTurnstileState,
  getTurnstileLanguage,
  SLOW_AFTER_MS,
  type TurnstileEffect,
  type TurnstileEvent,
  type TurnstileState,
  type TurnstileSurface,
  transitionTurnstile,
} from "@/lib/auth/turnstile-state";
import { cn } from "@/lib/utils";

const APP_OWNED_SCRIPT_ATTRIBUTE = "data-teksta-turnstile-script";
const SCRIPT_STATE_ATTRIBUTE = "data-teksta-turnstile-state";
const TURNSTILE_SCRIPT_SRC = `${SCRIPT_URL}?render=explicit`;

type ScriptAttempt = {
  cleanup: () => void;
  element: HTMLScriptElement;
  generation: number;
};

export type TurnstileVerificationHandle = {
  resetAfterSubmission: () => void;
};

type TurnstileVerificationProps = {
  onTokenChange: (token: string | null) => void;
  siteKey: string;
  surface: TurnstileSurface;
};

type TurnstileVerificationViewProps = {
  onReload: () => void;
  onRetry: () => void;
  state: TurnstileState;
};

const now = () => performance.now();

const getBlockedMessageKey = (state: TurnstileState) => {
  switch (state.currentIncident?.kind) {
    case "configuration_error":
      return "auth.turnstileConfigurationMessage";
    case "unsupported":
      return "auth.turnstileUnsupportedMessage";
    default:
      return "auth.turnstileUnavailableMessage";
  }
};

const getStatusMessageKey = (state: TurnstileState) => {
  if (state.phase === "token_ready" && state.hadIncident) return "auth.turnstileReadyMessage";
  if (state.phase === "recovering" || state.manualRetryPending) {
    return "auth.turnstileRetryingMessage";
  }
  if (state.currentIncident?.kind === "expired") return "auth.turnstileRetryingMessage";
  if (state.currentIncident?.kind === "slow") return "auth.turnstileSlowMessage";
  return null;
};

export const TurnstileVerificationView = ({
  onReload,
  onRetry,
  state,
}: TurnstileVerificationViewProps) => {
  const t = useTranslations();
  const isBlocked = state.phase === "blocked";
  const retryableBlocked = isBlocked && state.currentIncident?.retryable === true;
  const showRetry =
    state.manualRetryPending ||
    state.phase === "recovering" ||
    state.currentIncident?.kind === "slow" ||
    retryableBlocked;
  const retryBusy = state.manualRetryPending;
  const showReload = retryableBlocked && state.manualRetryAttempted;
  const statusMessageKey = getStatusMessageKey(state);

  return (
    <div className="space-y-3">
      <div
        role="status"
        aria-atomic="true"
        aria-busy={retryBusy || undefined}
        className={cn("min-h-5 text-muted-foreground text-sm", !statusMessageKey && "sr-only")}
      >
        {statusMessageKey ? t(statusMessageKey) : ""}
      </div>

      {isBlocked ? (
        <Alert variant="destructive">
          <AlertTitle>
            {state.currentIncident?.kind === "unsupported"
              ? t("auth.turnstileUnsupportedTitle")
              : t("auth.turnstileUnavailableTitle")}
          </AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{t(getBlockedMessageKey(state))}</p>
            {state.surface === "sign_up" ? <p>{t("auth.turnstileGoogleFallbackMessage")}</p> : null}
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="flex flex-col gap-2 sm:flex-row">
        {showRetry ? (
          <Button
            type="button"
            variant="outline"
            size="lg"
            aria-busy={retryBusy || undefined}
            aria-disabled={retryBusy || undefined}
            className={cn(retryBusy && "pointer-events-none opacity-50")}
            onClick={() => {
              if (!retryBusy) onRetry();
            }}
          >
            {t("auth.turnstileTryAgainButton")}
          </Button>
        ) : null}
        {showReload ? (
          <Button type="button" variant="ghost" size="lg" onClick={onReload}>
            {t("auth.turnstileReloadButton")}
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export const TurnstileVerification = forwardRef<
  TurnstileVerificationHandle,
  TurnstileVerificationProps
>(({ onTokenChange, siteKey, surface }, ref) => {
  const locale = useLocale();
  const posthog = usePostHog();
  const [state, setState] = useState(() => createTurnstileState(surface));
  const stateRef = useRef(state);
  const onTokenChangeRef = useRef(onTokenChange);
  const mountedRef = useRef(false);
  const scriptAttemptRef = useRef<ScriptAttempt | null>(null);
  const scriptRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const turnstileRef = useRef<TurnstileInstance | undefined>(undefined);
  const dispatchRef = useRef<(event: TurnstileEvent) => TurnstileState>(() => stateRef.current);

  onTokenChangeRef.current = onTokenChange;

  const emitEffects = useCallback(
    (effects: TurnstileEffect[]) => {
      for (const effect of effects) {
        if (effect.type === "analytics") {
          posthog?.capture(effect.event, effect.properties);
          continue;
        }
        if (effect.type === "failure") {
          posthog?.capture("auth_turnstile_failed", effect.properties);
          if (surface === "sign_up") {
            posthog?.capture("signup_turnstile_failed", {
              reason:
                effect.properties.source === "timeout"
                  ? "timeout"
                  : effect.properties.source === "unsupported"
                    ? "unsupported"
                    : "error",
            });
          }
          Sentry.captureMessage("Turnstile blocked an auth form", {
            extra: {
              attempt_bucket: effect.properties.attempt_bucket,
              error_code: effect.properties.error_code,
              ...Object.fromEntries(
                Object.entries(effect.properties).filter(([key]) => key.endsWith("_ms"))
              ),
            },
            level: "warning",
            tags: {
              error_family: String(effect.properties.error_family),
              retryable: String(effect.properties.retryable),
              source: String(effect.properties.source),
              surface,
            },
          });
          continue;
        }
        if (scriptRetryTimerRef.current) clearTimeout(scriptRetryTimerRef.current);
        scriptRetryTimerRef.current = setTimeout(() => {
          scriptRetryTimerRef.current = null;
          dispatchRef.current({
            generation: effect.generation,
            now: now(),
            type: "script_retry_started",
          });
        }, effect.delayMs);
      }
    },
    [posthog, surface]
  );

  const dispatch = useCallback(
    (event: TurnstileEvent) => {
      if (!mountedRef.current) return stateRef.current;
      const transition = transitionTurnstile(stateRef.current, event);
      stateRef.current = transition.state;
      setState(transition.state);
      emitEffects(transition.effects);
      return transition.state;
    },
    [emitEffects]
  );
  dispatchRef.current = dispatch;

  const removeCurrentScript = useCallback((generation: number) => {
    const attempt = scriptAttemptRef.current;
    if (!attempt || attempt.generation !== generation) return;
    attempt.cleanup();
    if (
      attempt.element.getAttribute(APP_OWNED_SCRIPT_ATTRIBUTE) === "true" &&
      attempt.element.isConnected
    ) {
      attempt.element.remove();
    }
    scriptAttemptRef.current = null;
  }, []);

  const startScript = useCallback(
    (generation: number) => {
      if (!mountedRef.current || generation !== stateRef.current.scriptGeneration) return;
      if (scriptAttemptRef.current?.generation === generation) return;

      const existing = document.getElementById(DEFAULT_SCRIPT_ID);
      if (
        existing instanceof HTMLScriptElement &&
        !window.turnstile &&
        existing.getAttribute(APP_OWNED_SCRIPT_ATTRIBUTE) === "true" &&
        existing.getAttribute(SCRIPT_STATE_ATTRIBUTE) === "failed"
      ) {
        existing.remove();
        dispatchRef.current({ generation, now: now(), type: "script_error" });
        return;
      }
      const element =
        existing instanceof HTMLScriptElement ? existing : document.createElement("script");
      let cleanedUp = false;

      const cleanup = () => {
        if (cleanedUp) return;
        cleanedUp = true;
        element.removeEventListener("load", handleLoad);
        element.removeEventListener("error", handleError);
      };
      const handleLoad = () => {
        cleanup();
        dispatchRef.current({ generation, now: now(), type: "script_loaded" });
      };
      const handleError = () => {
        cleanup();
        if (generation !== stateRef.current.scriptGeneration) {
          dispatchRef.current({ generation, now: now(), type: "script_error" });
          return;
        }
        removeCurrentScript(generation);
        dispatchRef.current({ generation, now: now(), type: "script_error" });
      };

      element.addEventListener("load", handleLoad, { once: true });
      element.addEventListener("error", handleError, { once: true });
      scriptAttemptRef.current = { cleanup, element, generation };

      if (window.turnstile && existing) {
        handleLoad();
        return;
      }

      if (!existing) {
        element.id = DEFAULT_SCRIPT_ID;
        element.src = TURNSTILE_SCRIPT_SRC;
        element.async = true;
        element.defer = true;
        element.setAttribute(APP_OWNED_SCRIPT_ATTRIBUTE, "true");
        element.setAttribute(SCRIPT_STATE_ATTRIBUTE, "loading");
        element.addEventListener(
          "load",
          () => element.setAttribute(SCRIPT_STATE_ATTRIBUTE, "ready"),
          { once: true }
        );
        element.addEventListener(
          "error",
          () => element.setAttribute(SCRIPT_STATE_ATTRIBUTE, "failed"),
          { once: true }
        );
        document.head.appendChild(element);
      }
    },
    [removeCurrentScript]
  );

  useEffect(() => {
    mountedRef.current = true;
    dispatchRef.current({ now: now(), type: "start" });
    return () => {
      mountedRef.current = false;
      if (scriptRetryTimerRef.current) {
        clearTimeout(scriptRetryTimerRef.current);
        scriptRetryTimerRef.current = null;
      }
      scriptAttemptRef.current?.cleanup();
      scriptAttemptRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (state.started && state.phase === "loading_script") {
      startScript(state.scriptGeneration);
    }
  }, [startScript, state.phase, state.scriptGeneration, state.started]);

  useEffect(() => {
    if (!state.started || state.tokenReady || state.phase === "blocked") return;
    const attemptStartedAt = state.attemptStartedAt;
    const remaining = Math.max(0, attemptStartedAt + SLOW_AFTER_MS - now());
    const timer = setTimeout(() => {
      dispatchRef.current({ attemptStartedAt, now: now(), type: "slow_timeout" });
    }, remaining);
    return () => clearTimeout(timer);
  }, [state.attemptStartedAt, state.phase, state.started, state.tokenReady]);

  useEffect(() => {
    if (state.recoveryDeadlineAt === null) return;
    const deadline = state.recoveryDeadlineAt;
    const timer = setTimeout(
      () => {
        dispatchRef.current({ deadline, now: now(), type: "recovery_deadline" });
      },
      Math.max(0, deadline - now())
    );
    return () => clearTimeout(timer);
  }, [state.recoveryDeadlineAt]);

  const handleManualRetry = useCallback(() => {
    if (stateRef.current.manualRetryPending) return;
    if (scriptRetryTimerRef.current) {
      clearTimeout(scriptRetryTimerRef.current);
      scriptRetryTimerRef.current = null;
    }
    const previousGeneration = stateRef.current.scriptGeneration;
    const hasWidget = stateRef.current.hasWidget && Boolean(turnstileRef.current);
    if (!hasWidget) {
      if (window.turnstile) {
        scriptAttemptRef.current?.cleanup();
        scriptAttemptRef.current = null;
      } else {
        removeCurrentScript(previousGeneration);
      }
    }
    dispatch({ hasWidget, now: now(), type: "manual_retry" });
    if (hasWidget) turnstileRef.current?.reset();
  }, [dispatch, removeCurrentScript]);

  useImperativeHandle(
    ref,
    () => ({
      resetAfterSubmission: () => {
        onTokenChangeRef.current(null);
        dispatch({ now: now(), type: "submission_reset" });
        turnstileRef.current?.reset();
      },
    }),
    [dispatch]
  );

  const handleSuccess = useCallback(
    (token: string) => {
      dispatch({ now: now(), type: "success" });
      onTokenChangeRef.current(token);
    },
    [dispatch]
  );
  const handleExpire = useCallback(() => {
    onTokenChangeRef.current(null);
    dispatch({ now: now(), type: "expired" });
  }, [dispatch]);
  const handleError = useCallback(
    (errorCode: string) => {
      onTokenChangeRef.current(null);
      dispatch({ errorCode, now: now(), type: "widget_error" });
    },
    [dispatch]
  );
  const handleTimeout = useCallback(() => {
    onTokenChangeRef.current(null);
    dispatch({ now: now(), type: "widget_timeout" });
  }, [dispatch]);
  const handleUnsupported = useCallback(() => {
    onTokenChangeRef.current(null);
    dispatch({ now: now(), type: "unsupported" });
  }, [dispatch]);

  return (
    <div className="space-y-3">
      {state.scriptLoadedAt !== null ? (
        <Turnstile
          ref={turnstileRef}
          injectScript={false}
          scriptOptions={{ id: DEFAULT_SCRIPT_ID }}
          siteKey={siteKey}
          onError={handleError}
          onExpire={handleExpire}
          onSuccess={handleSuccess}
          onTimeout={handleTimeout}
          onUnsupported={handleUnsupported}
          onWidgetLoad={() => dispatch({ now: now(), type: "widget_loaded" })}
          options={{
            appearance: "interaction-only",
            language: getTurnstileLanguage(locale),
            refreshExpired: "auto",
            refreshTimeout: "auto",
            retry: "auto",
            retryInterval: 8_000,
            theme: "dark",
          }}
        />
      ) : null}
      <TurnstileVerificationView
        state={state}
        onReload={() => window.location.reload()}
        onRetry={handleManualRetry}
      />
    </div>
  );
});

TurnstileVerification.displayName = "TurnstileVerification";
